import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { TableInfo } from '../../class/TableInfo';
import { SharedService } from '../../services/shared';
import { Connect } from '../../services/connect';
import { ToastrService } from 'ngx-toastr';
import { GoogleMapFilter } from '../../class/Filter/GoogleMapFilter';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Conditions } from '../../class/Conditions';
import { Condition } from '../../class/Condition';

@Component({
  selector: 'google-map-view',
  templateUrl: './google-map-view.component.html',
  styleUrls: ['./google-map-view.component.scss']
})
export class GoogleMapViewComponent implements OnInit {
  @Input() table_info: TableInfo;
  @Input() edittingCustomFilter: GoogleMapFilter;
  @Output() onClickCancelButton = new EventEmitter<void>();
  @Output() onClickSaveButton = new EventEmitter<GoogleMapFilter>();
  @Output() onClickPreviewButton = new EventEmitter<GoogleMapFilter>();

  // タブ関連のプロパティ
  public activeTab: string = 'basic'; // デフォルトで基本設定タブを選択
  
  // マーカー色のリスト
  private allColors = [
    { value: 'red', label: '赤' },
    { value: 'blue', label: '青' },
    { value: 'green', label: '緑' },
    { value: 'yellow', label: '黄' },
    { value: 'purple', label: '紫' },
    { value: 'pink', label: 'ピンク' },
    { value: 'orange', label: 'オレンジ' },
    { value: 'ltblue', label: 'ライトブルー' }
  ];
  
  // ピン型で使用可能な色リスト（緑、黄、オレンジ、ライトブルーは使用不可）
  private pushpinColors = [
    { value: 'red', label: '赤' },
    { value: 'blue', label: '青' },
    { value: 'purple', label: '紫' },
    { value: 'pink', label: 'ピンク' }
  ];

  constructor(
    private _share: SharedService,
    private _connect: Connect,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    if (!this.edittingCustomFilter) {
      this.edittingCustomFilter = new GoogleMapFilter();
    }
  }

  preview() {
    this.onClickPreviewButton.emit(this.edittingCustomFilter);
  }

  saveChart() {
    if (!this.edittingCustomFilter.name) {
      this.toastr.error('タイトルを入力してください。', 'エラー');
      return;
    }

    if (!this.edittingCustomFilter.address_field) {
      this.toastr.error('住所フィールドを選択してください。', 'エラー');
      return;
    }

    this.onClickSaveButton.emit(this.edittingCustomFilter);
  }

  // タブ切り替えメソッド
  changeTab(tabId: string) {
    this.activeTab = tabId;
  }

  // ドラッグアンドドロップの処理
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        this.edittingCustomFilter.info_modal_fields,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  // 表示項目が変更された時の処理
  onInfoModalFieldsChanged(event: { selected_field_name_a: string[] }) {
    if (event.selected_field_name_a.length > 10) {
      this.toastr.error('表示項目は10個までしか選択できません。', 'エラー');
      return;
    }
    this.edittingCustomFilter.info_modal_fields = event.selected_field_name_a;
  }

  // マーカーの色設定を追加
  addMarkerColor() {
    this.edittingCustomFilter.addMarkerColorWithConditions();
  }

  // マーカーアイコンのプレビューURLを取得
  getMarkerIconPreviewUrl(colorSetting: any): string {
    if (!colorSetting) {
      return 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
    }
    
    const color = colorSetting.color || 'red';
    const iconType = colorSetting.iconType || 'dot';
    
    // サポートするアイコンタイプのみ対応（dot と pushpin のみ）
    if (iconType === 'pushpin') {
      return `https://maps.google.com/mapfiles/ms/icons/${color}-pushpin.png`;
    } else {
      // その他の場合はデフォルトの丸型を表示
      return `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
    }
  }

  // マーカーの色設定を削除
  removeMarkerColor(index: number) {
    this.edittingCustomFilter.removeMarkerColor(index);
  }

  // 条件を追加
  addCondition(colorSetting: any) {
    // colorSettingとconditionsが存在するか確認
    if (!colorSetting) {
      console.error('colorSetting is undefined or null');
      return;
    }
    
    if (!colorSetting.conditions) {
      // 条件オブジェクトが存在しない場合は新しく作成
      colorSetting.conditions = new Conditions();
    }
    
    try {
      // 新しい条件を追加（デフォルト値を設定）
      const newCondition = new Condition('eq', 'id', '');
      colorSetting.conditions.condition_a.push(newCondition);
    } catch (error) {
      console.error('Error adding condition:', error);
      // エラー発生時は条件オブジェクトを作り直して追加
      colorSetting.conditions = new Conditions();
      colorSetting.conditions.addCondition('eq', 'id', '');
    }
  }

  // 条件を削除
  removeCondition(colorSetting: any, conditionIndex: number) {
    if (!colorSetting || !colorSetting.conditions) {
      console.error('colorSetting or conditions is undefined');
      return;
    }
    
    try {
      colorSetting.conditions.deleteCondition(conditionIndex);
    } catch (error) {
      console.error('Error removing condition:', error);
    }
  }

  // 条件が変更された時の処理
  onConditionChanged(event: any, colorSettingIndex: number) {
    if (!event || !this.edittingCustomFilter || !this.edittingCustomFilter.marker_colors) {
      console.error('Event or marker_colors array is undefined');
      return;
    }
    
    // インデックスチェック
    if (colorSettingIndex < 0 || colorSettingIndex >= this.edittingCustomFilter.marker_colors.length) {
      console.error('Invalid colorSettingIndex:', colorSettingIndex);
      return;
    }
    
    const colorSetting = this.edittingCustomFilter.marker_colors[colorSettingIndex];
    if (colorSetting && colorSetting.conditions) {
      try {
        // イベントから条件のインデックスと更新された条件を取得
        const { index, condition } = event;
        
        // インデックスチェック
        if (index < 0 || !Array.isArray(colorSetting.conditions.condition_a) || 
            index >= colorSetting.conditions.condition_a.length) {
          console.error('Invalid condition index:', index);
          return;
        }
        
        // 対応する条件を更新
        colorSetting.conditions.condition_a[index] = condition;
        console.log('Updated conditions:', colorSetting.conditions.condition_a);
      } catch (error) {
        console.error('Error updating condition:', error);
      }
    } else {
      console.error('ColorSetting or conditions is undefined');
    }
  }

  // 保存処理
  save() {
    if (!this.edittingCustomFilter) {
      this.toastr.error('設定が見つかりません。', 'エラー');
      return;
    }

    if (!this.edittingCustomFilter.name) {
      this.toastr.error('フィルター名を入力してください。', 'エラー');
      return;
    }

    // 既存のフィルターを更新する場合はIDを含める
    const params = {
      table: this.table_info.table,
      params_json: JSON.stringify(this.edittingCustomFilter.toArray())
    };
    if (this.edittingCustomFilter.id) {
      params['id'] = this.edittingCustomFilter.id;
    }

    this._connect.post('/admin/save-google-map-filter', params).subscribe(
      (response) => {
        if (response['success']) {
          // レスポンスからフィルター情報を取得
          const savedFilter = response['filter'];
          if (savedFilter) {
            // フィルターIDを更新
            this.edittingCustomFilter.id = savedFilter.id;
            // パラメータを更新
            const params = JSON.parse(savedFilter.params_json);
            Object.assign(this.edittingCustomFilter, params);
          }
          
          this.toastr.success('マーカーの色設定を保存しました。', '成功');
          
          // 保存したフィルターを適用して表示を更新
          this.onClickPreviewButton.emit(this.edittingCustomFilter);
          this.onClickCancelButton.emit(); // モーダルを閉じる
        } else {
          this.toastr.error(response['message'] || '保存に失敗しました。', 'エラー');
        }
      },
      (error) => {
        this.toastr.error('保存中にエラーが発生しました。', 'エラー');
        console.error('Save error:', error);
      }
    );
  }

  // マーカーの種類に基づいて利用可能な色のリストを取得
  getAvailableColors(iconType: string): any[] {
    if (iconType === 'pushpin') {
      return this.pushpinColors;
    } else {
      return this.allColors;
    }
  }
  
  // 色が現在のマーカータイプで使用可能かチェック
  isColorAvailableForIconType(color: string, iconType: string): boolean {
    if (iconType !== 'pushpin') {
      return true; // ドット型は全ての色が使用可能
    }
    
    // ピン型の場合は使用可能な色のみTrue
    return this.pushpinColors.some(c => c.value === color);
  }
  
  // マーカーの種類変更時に色も調整
  onIconTypeChanged(colorSetting: any) {
    if (colorSetting.iconType === 'pushpin' && !this.isColorAvailableForIconType(colorSetting.color, 'pushpin')) {
      // ピン型で使用できない色が選択されている場合は、赤に変更
      colorSetting.color = 'red';
    }
  }
} 